// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import StrapiImageRenderer from '../StrapiImageRenderer';
import { Icon } from '@/components/common/icons';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { DefaultSession } from 'next-auth';
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { UploadFile, ComponentElementsFileCard as ElementsFileCardProps } from '@/types/graphql';

const IMAGE_EXTS = new Set(['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'AVIF', 'BMP', 'SVG']);

type ComponentElementsFileCardProps = Omit<StrapiComponentElementProps<ElementsFileCardProps>, 'file' | 'fileName'> & {
	file: UploadFile;
	fileName: string;
	currentUser?: DefaultSession['user'] & { role?: string; groups?: string[] };
	fileBlockAuthenticated?: boolean;
	isAuthenticated?: boolean;
	accessRestriction?: string | null;
	isLocked?: boolean;
	parentResolved?: boolean;
	host?: string;
	selected?: boolean;
	onSelectedChange?: (selected: boolean, file: UploadFile) => void;
	showPreview?: boolean;
	sx?: SxProps<Theme>;
	userGroups?: string[];
	fetchedGroupPks?: string[];
	isPermissionsLoading?: boolean;
};

const ComponentElementsFileCard = ({
	host,
	currentUser,
	file,
	fileName,
	fileBlockAuthenticated = false,
	isAuthenticated = false,
	accessRestriction = null,
	isLocked = false,
	parentResolved = false,
	selected,
	onSelectedChange,
	showPreview = false,
	sx,
	userGroups = [],
	fetchedGroupPks = [],
	isPermissionsLoading = false
}: ComponentElementsFileCardProps) => {
	const theme = useTheme();
	const [internalSelected, setInternalSelected] = React.useState(false);

	const isSelected = selected ?? internalSelected;
	const isUserLoggedIn = Boolean(currentUser && (currentUser.email || currentUser.id));
	const loginRequired = fileBlockAuthenticated || isAuthenticated;
	const authRulesPass = loginRequired ? isUserLoggedIn : true;

	// Permissions now calculated purely from props
	const groupRulesPass = React.useMemo(() => {
		if (parentResolved) return true;
		if (!accessRestriction) return true;
		if (!loginRequired) return true;
		if (isPermissionsLoading) return false;

		let target: string[] = [];
		try {
			const parsed = JSON.parse(accessRestriction);
			target = Array.isArray(parsed)
				? parsed.map((item) => String(item).toLowerCase().trim())
				: [String(parsed).toLowerCase().trim()];
		} catch {
			target = [String(accessRestriction).toLowerCase().trim()];
		}

		const userGroupsNorm = (userGroups || []).map((g) => String(g).toLowerCase().trim());
		const fetched = (fetchedGroupPks || []).map((pk) => String(pk).toLowerCase().trim());

		return fetched.some((pk) => target.includes(pk)) || target.some((name) => userGroupsNorm.includes(name));
	}, [parentResolved, accessRestriction, loginRequired, isPermissionsLoading, userGroups, fetchedGroupPks]);

	const canDownload = parentResolved ? !isLocked : !isPermissionsLoading && authRulesPass && groupRulesPass;

	const extension = React.useMemo(() => {
		if (file.ext) return file.ext.replace('.', '').toUpperCase();
		const raw = fileName.split('.').pop();
		return raw ? raw.toUpperCase() : 'DATEI';
	}, [file.ext, fileName]);

	const uploadedDate = React.useMemo(() => {
		if (!file.updatedAt) return '';
		const parsed = new Date(file.updatedAt as string | number | Date);
		if (Number.isNaN(parsed.getTime())) return String(file.updatedAt);
		return new Intl.DateTimeFormat('de-DE').format(parsed);
	}, [file.updatedAt]);

	const metaLine = uploadedDate ? `${extension} | ${uploadedDate}` : extension;
	const isImageFile = IMAGE_EXTS.has(extension);

	// ── Handlers ──
	const handleSelectedChange = (next: boolean) => {
		if (!canDownload) return;
		if (onSelectedChange) onSelectedChange(next, file);
		else setInternalSelected(next);
	};

	const handleDownload = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!canDownload) {
			console.warn('You do not have permission to download this file.');
			return;
		}

		const a = document.createElement('a');
		a.href = file?.url;
		a.download = fileName;
		a.rel = 'noopener';
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	const handlePreview = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!canDownload) {
			console.warn('You do not have permission to preview this file.');
			return;
		}
		window.open(file?.previewUrl ?? file?.url, '_blank', 'noopener,noreferrer');
	};

	const getRestrictionMessage = () => {
		if (!parentResolved && isPermissionsLoading) return 'Prüfe Berechtigung...';
		if (canDownload) return null;
		if (!isUserLoggedIn && loginRequired) return 'Zum Download anmelden';
		return 'Keine Berechtigung';
	};

	// ── Render Templates ──
	const hiddenCheckboxInput = (
		<input
			type="checkbox"
			checked={isSelected}
			disabled={!canDownload}
			onChange={(e) => handleSelectedChange(e.target.checked)}
			aria-label={`${fileName} auswählen`}
			style={{
				position: 'absolute',
				opacity: 0,
				width: 0,
				height: 0,
				margin: 0
			}}
		/>
	);

	const checkboxVisual = (
		<Box
			component="span"
			sx={{
				display: 'block',
				flexShrink: 0,
				width: 18,
				height: 18,
				borderRadius: '4px',
				border: !canDownload ? `1.4px solid ${theme.palette.grey[300]}` : isSelected ? 'none' : '1.4px solid #353535',
				backgroundColor: !canDownload
					? theme.palette.grey[100]
					: isSelected
						? theme.palette.primary.main
						: 'transparent',
				boxSizing: 'border-box',
				position: 'relative',
				cursor: canDownload ? 'pointer' : 'not-allowed'
			}}
		>
			{isSelected && canDownload && (
				<svg
					viewBox="0 0 18 18"
					width="18"
					height="18"
					style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}
				>
					<title>{`${fileName} ausgewählt`}</title>
					<polyline
						points="3.5,9 7,12.5 14.5,5"
						fill="none"
						stroke="white"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)}
		</Box>
	);

	const previewImage = (
		<Box
			sx={{
				width: 40,
				borderRadius: 1,
				overflow: 'hidden',
				backgroundColor: theme.palette.grey[100],
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				opacity: canDownload ? 1 : 0.5
			}}
		>
			{isImageFile || file?.previewUrl ? (
				<StrapiImageRenderer
					src={isImageFile ? (file?.previewUrl ?? file?.url ?? undefined) : (file?.previewUrl ?? undefined)}
					imageData={file}
					host={host}
					fill
					sizes="248px"
					alt={fileName}
					imageFit="contain"
				/>
			) : (
				<Typography variant="body-sm" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
					{extension}
				</Typography>
			)}
		</Box>
	);

	const metaTextSx = {
		display: 'block',
		color: canDownload ? '#353535' : theme.palette.text.disabled,
		fontFamily: '"DIN Next LT Pro"',
		fontSize: '0.875rem',
		fontWeight: 400,
		lineHeight: '1rem',
		letterSpacing: '-0.05px',
		transform: 'translateY(1.5px)'
	} as const;

	const actionIconButtonSx = {
		color: canDownload ? theme.palette.text.secondary : theme.palette.grey[300],
		p: 0.5,
		borderRadius: 1,
		cursor: canDownload ? 'pointer' : 'not-allowed',
		transition: theme.transitions.create(['color', 'background-color'], {
			duration: theme.transitions.duration.shorter
		}),
		'&:hover': {
			color: canDownload ? theme.palette.primary.main : theme.palette.grey[300],
			backgroundColor: 'transparent'
		},
		'&:active': { backgroundColor: 'transparent' }
	} as const;

	return (
		<Box
			sx={[
				{
					backgroundColor: theme.palette.common.white,
					border: `1px solid ${theme.palette.grey[200]}`,
					borderRadius: 2,
					width: showPreview ? '100%' : 240,
					maxWidth: showPreview ? { md: 632 } : 'none',
					height: { xs: 'auto', md: showPreview ? 160 : 168 },
					overflow: { xs: 'visible', md: showPreview ? 'hidden' : 'visible' },
					px: showPreview ? 6 : 4,
					pt: showPreview ? 6 : 4,
					pb: showPreview ? 6 : 2,
					display: 'flex',
					flexDirection: 'column',
					gap: showPreview ? 3 : 6,
					boxSizing: 'border-box',
					opacity: canDownload ? 1 : 0.6,
					transition: theme.transitions.create(['border-color', 'box-shadow', 'opacity'], {
						duration: theme.transitions.duration.shorter
					}),
					'&:hover': {
						borderColor: canDownload ? theme.palette.primary.main : theme.palette.grey[200]
					}
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			{showPreview ? (
				<React.Fragment>
					{/* ── Mobile Layout ── */}
					<Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: 0 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 2
							}}
						>
							<Box
								component="label"
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
									cursor: canDownload ? 'pointer' : 'not-allowed',
									minWidth: 0
								}}
							>
								{hiddenCheckboxInput}
								{checkboxVisual}
								<Box component="span" sx={metaTextSx}>
									{metaLine}
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 3,
									flexShrink: 0
								}}
							>
								<IconButton
									size="small"
									onClick={handleDownload}
									disabled={!canDownload}
									aria-label={`${fileName} herunterladen`}
									sx={actionIconButtonSx}
								>
									<Icon name="download" />
								</IconButton>
								<IconButton
									size="small"
									onClick={handlePreview}
									disabled={!canDownload}
									aria-label={`${fileName} ansehen`}
									sx={actionIconButtonSx}
								>
									<Icon name="eye" />
								</IconButton>
							</Box>
						</Box>

						<Typography
							variant="body-sm"
							sx={{
								color: canDownload ? theme.palette.text.primary : theme.palette.text.secondary,
								fontWeight: 500,
								mt: 6
							}}
						>
							{fileName}
						</Typography>

						{!canDownload && (
							<Typography variant="body-sm" sx={{ color: theme.palette.error.main, fontWeight: 500, mt: 2 }}>
								{getRestrictionMessage()}
							</Typography>
						)}
					</Box>

					{/* ── Desktop Layout (CSS Grid) ── */}
					<Box
						sx={{
							display: { xs: 'none', md: 'grid' },
							gridTemplateColumns: '18px 40px 40px 2rem 1px 2rem 1fr auto',
							gridTemplateRows: 'auto auto',
							rowGap: { md: 2, lg: 3 },
							minWidth: 0,
							flex: 1
						}}
					>
						<Box
							component="label"
							sx={{
								gridColumn: 1,
								gridRow: 1,
								alignSelf: 'center',
								display: 'flex',
								alignItems: 'center',
								cursor: canDownload ? 'pointer' : 'not-allowed'
							}}
						>
							{hiddenCheckboxInput}
							{checkboxVisual}
						</Box>

						<Box
							component="span"
							sx={{
								gridColumn: 7,
								gridRow: 1,
								alignSelf: 'center',
								...metaTextSx
							}}
						>
							{metaLine}
						</Box>

						<Box
							sx={{
								gridColumn: 8,
								gridRow: 1,
								alignSelf: 'center',
								display: 'flex',
								alignItems: 'center',
								gap: 3
							}}
						>
							<IconButton
								size="small"
								onClick={handleDownload}
								disabled={!canDownload}
								aria-label={`${fileName} herunterladen`}
								sx={actionIconButtonSx}
							>
								<Icon name="download" />
							</IconButton>
							<IconButton
								size="small"
								onClick={handlePreview}
								disabled={!canDownload}
								aria-label={`${fileName} ansehen`}
								sx={actionIconButtonSx}
							>
								<Icon name="eye" />
							</IconButton>
						</Box>

						<Box sx={{ gridColumn: 3, gridRow: 2, alignSelf: 'stretch' }}>{previewImage}</Box>
						<Box
							sx={{
								gridColumn: 5,
								gridRow: 2,
								alignSelf: 'stretch',
								width: '1px',
								background: 'rgba(0, 0, 0, 0.18)',
								opacity: canDownload ? 1 : 0.5
							}}
						/>

						<Box
							sx={{
								gridColumn: 7,
								gridRow: 2,
								minWidth: 0,
								overflow: 'hidden'
							}}
						>
							<Typography
								variant="body-sm"
								sx={{
									color: canDownload ? theme.palette.text.primary : theme.palette.text.secondary,
									fontWeight: 500,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap'
								}}
							>
								{fileName}
							</Typography>

							{!canDownload && (
								<Typography
									variant="body-sm"
									sx={{
										color: theme.palette.error.main,
										fontWeight: 500,
										mt: 1
									}}
								>
									{getRestrictionMessage()}
								</Typography>
							)}
						</Box>
					</Box>
				</React.Fragment>
			) : (
				<React.Fragment>
					{/* ── Grid/Default Mode ── */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 2
						}}
					>
						<Box
							component="label"
							sx={{
								display: 'flex',
								alignItems: 'center',
								cursor: canDownload ? 'pointer' : 'not-allowed'
							}}
						>
							{hiddenCheckboxInput}
							{checkboxVisual}
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
							<IconButton
								size="small"
								onClick={handleDownload}
								disabled={!canDownload}
								aria-label={`${fileName} herunterladen`}
								sx={actionIconButtonSx}
							>
								<Icon name="download" />
							</IconButton>
							<IconButton
								size="small"
								onClick={handlePreview}
								disabled={!canDownload}
								aria-label={`${fileName} ansehen`}
								sx={actionIconButtonSx}
							>
								<Icon name="eye" />
							</IconButton>
						</Box>
					</Box>
					<Stack spacing={2} sx={{ minWidth: 0 }}>
						<Typography
							variant="body-sm"
							sx={{
								color: canDownload ? theme.palette.text.primary : theme.palette.text.secondary,
								fontWeight: 500
							}}
						>
							{fileName}
						</Typography>

						{!canDownload && (
							<Typography variant="body-sm" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
								{getRestrictionMessage()}
							</Typography>
						)}
					</Stack>
				</React.Fragment>
			)}
		</Box>
	);
};

export default ComponentElementsFileCard;
